import { useMutation, useQuery } from "@apollo/client/react";
import { throwForValidationResult } from "@/lib/graphql-error";
import { LocalizedError } from "@/lib/localized-error";
import { useIntl, type IntlShape } from "react-intl";
import {
  CreateEducationDocument,
  DeleteEducationDocument,
  MyselfExpertiseDataDocument,
  UpdateEducationDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { recipientRelations, type ContentRecipient } from "@/lib/content-sharing";
import { toDateInstant } from "@/lib/date-instant";

export interface EducationFormInput {
  field: string;
  institution: string;
  degree: string;
  city: string;
  startDate: string;
  endDate: string;
  country?: string;
  sharedWith?: ReadonlyArray<ContentRecipient | string>;
  publiclyReadable?: boolean;
  allGroups?: boolean;
}

export type EducationItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["educations"]
>[number];

export interface UseEducationResult {
  items: readonly EducationItem[];
  /** Resolves with the new row's id. */
  add: (input: EducationFormInput) => Promise<string>;
  update: (id: string, input: EducationFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toDataInput(input: EducationFormInput, intl: IntlShape) {
  const startDate = toDateInstant(input.startDate);
  if (!startDate) {
    throw new LocalizedError(
      intl.formatMessage({ id: "profile.page.error.startDateRequired", defaultMessage: "Start date is required." }),
    );
  }
  if (!input.field.trim()) {
    throw new LocalizedError(
      intl.formatMessage({ id: "profile.page.error.fieldOfStudyRequired", defaultMessage: "Field of study is required." }),
    );
  }
  return {
    field: input.field,
    institution: input.institution || null,
    degree: input.degree ? { id: input.degree } : null,
    city: input.city || null,
    startDate,
    endDate: toDateInstant(input.endDate),
    ...(input.country !== undefined
      ? { country: input.country ? { id: input.country } : null }
      : {}),
    ...(input.allGroups !== undefined ? { allGroups: input.allGroups } : {}),
    ...(input.publiclyReadable !== undefined ? { publiclyReadable: input.publiclyReadable } : {}),
    ...(input.sharedWith !== undefined
      ? { sharedWith: recipientRelations(input.sharedWith) }
      : {}),
  };
}

export function useEducation(): UseEducationResult {
  const intl = useIntl();
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  const saveFailed = intl.formatMessage({ id: "profile.page.error.saveEducation", defaultMessage: "Could not save the education entry" });

  const [createMut, createState] = useMutation(CreateEducationDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateEducationDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteEducationDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: EducationFormInput) {
    if (!personId) throw new Error("useEducation: no personId yet");
    const created = await createMut({
      variables: {
        input: { data: { ...toDataInput(input, intl), person: { id: personId } } },
      },
    });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = created.data?.createEducation;
    if (out?.__typename !== "CreateEducationResult") {
      throwForValidationResult(out, saveFailed);
    }
    return out.result.id;
  }

  async function update(id: string, input: EducationFormInput) {
    const updated = await updateMut({ variables: { id, input: { data: toDataInput(input, intl) } } });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = updated.data?.updateEducation;
    if (out?.__typename !== "UpdateEducationResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.educations ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
